import { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import {
  Activity, XCircle, Clock, DollarSign, Cpu,
  StopCircle, Download, Volume2, Music, Layers, GitMerge, BarChart3,
  CheckCircle, Loader2, Play, Pause, Square, FolderArchive, ChevronDown, ChevronRight
} from 'lucide-react'
import { getJob, cancelJob, getJobResults, getStemsZipUrl, getMidiZipUrl, type Job, type SampleResult } from '../lib/api'
import { useWebSocket } from '../hooks/useWebSocket'
import { formatDuration, formatCurrency, MODEL_INFO } from '../lib/utils'
import { AudioPlayer, type AudioPlayerRef } from './AudioPlayer'
import { EnsembleComparison, type StemOverrides } from './EnsembleComparison'

interface JobDetailProps {
  jobId: string
}

interface EnsembleProgress {
  sampleIndex: number;
  sampleName: string;
  stepType: 'model' | 'merge';
  stepIndex: number;
  totalSteps: number;
  modelName: string | null;
  status: 'started' | 'completed';
  completedSteps: number[];
}

export function JobDetail({ jobId }: JobDetailProps) {
  const [job, setJob] = useState<Job | null>(null)
  const [results, setResults] = useState<SampleResult[]>([])
  const [loading, setLoading] = useState(true)
  const [cancelling, setCancelling] = useState(false)
  const [selectedSample, setSelectedSample] = useState<string | null>(null)
  const [showComparison, setShowComparison] = useState(false)
  const [ensembleProgress, setEnsembleProgress] = useState<EnsembleProgress | null>(null)
  const [isPlayingAll, setIsPlayingAll] = useState(false)
  const [stemOverrides, setStemOverrides] = useState<StemOverrides>({})
  const [expandedDrums, setExpandedDrums] = useState(true)  // Track if drums are expanded
  const playerRefs = useRef<Map<string, AudioPlayerRef>>(new Map())
  const { lastMessage } = useWebSocket(jobId)

  // Play all stems simultaneously
  const handlePlayAll = useCallback(() => {
    playerRefs.current.forEach((ref) => {
      ref.seek(0)
      ref.play()
    })
    setIsPlayingAll(true)
  }, [])

  // Pause all stems
  const handlePauseAll = useCallback(() => {
    playerRefs.current.forEach((ref) => {
      ref.pause()
    })
    setIsPlayingAll(false)
  }, [])

  // Stop all stems
  const handleStopAll = useCallback(() => {
    playerRefs.current.forEach((ref) => {
      ref.stop()
    })
    setIsPlayingAll(false)
  }, [])

  // Register player ref
  const registerPlayerRef = useCallback((name: string, ref: AudioPlayerRef | null) => {
    if (ref) {
      playerRefs.current.set(name, ref)
    } else {
      playerRefs.current.delete(name)
    }
  }, [])

  // Get effective file path for a stem (override or default)
  const getEffectiveStemPath = useCallback((stemName: string, defaultPath: string): string => {
    const override = stemOverrides[stemName]
    if (override) {
      return override.filePath
    }
    return defaultPath
  }, [stemOverrides])

  // Handle override change
  const handleOverrideChange = useCallback((newOverrides: StemOverrides) => {
    setStemOverrides(newOverrides)
    // Stop all playing audio when overrides change
    handleStopAll()
  }, [handleStopAll])

  // Clear player refs and overrides when sample changes
  useEffect(() => {
    playerRefs.current.clear()
    setIsPlayingAll(false)
    setStemOverrides({})
  }, [selectedSample])

  useEffect(() => {
    setLoading(true)
    setResults([])
    setSelectedSample(null)
    setEnsembleProgress(null)
    setIsPlayingAll(false)
    setStemOverrides({})
    playerRefs.current.clear()

    const fetchJob = async () => {
      try {
        const data = await getJob(jobId)
        setJob(data)
        
        if (data.status === 'completed') {
          const resultsData = await getJobResults(jobId)
          setResults(resultsData.results)
          if (resultsData.results.length > 0) {
            setSelectedSample(resultsData.results[0].sample_name)
          }
        }
      } catch {
        console.error('Failed to fetch job')
      } finally {
        setLoading(false)
      }
    }

    fetchJob()
    
    const interval = setInterval(async () => {
      try {
        const data = await getJob(jobId)
        setJob(data)
        
        if (data.status === 'completed') {
          const resultsData = await getJobResults(jobId)
          setResults(resultsData.results)
          if (resultsData.results.length > 0 && !selectedSample) {
            setSelectedSample(resultsData.results[0].sample_name)
          }
          clearInterval(interval)
        }
      } catch {
        console.error('Failed to refresh job')
      }
    }, 2000)
    
    return () => clearInterval(interval)
  }, [jobId])

  useEffect(() => {
    if (lastMessage?.event === 'job_progress' && lastMessage.job_id === jobId) {
      setJob(prev => prev ? {
        ...prev,
        progress: lastMessage.progress as Job['progress'],
        costs: lastMessage.costs as Job['costs']
      } : null)
    }

    // Handle ensemble step updates
    if (lastMessage?.event === 'ensemble_step' && lastMessage.job_id === jobId) {
      const stepIndex = lastMessage.step_index as number
      const status = lastMessage.status as 'started' | 'completed'
      const sampleIndex = lastMessage.sample_index as number

      setEnsembleProgress(prev => {
        // Reset completed steps if we moved to a new sample
        const isNewSample = !prev || prev.sampleIndex !== sampleIndex
        const completedSteps = isNewSample ? [] : [...(prev?.completedSteps || [])]

        // Add to completed steps if this step completed
        if (status === 'completed' && !completedSteps.includes(stepIndex)) {
          completedSteps.push(stepIndex)
        }

        return {
          sampleIndex,
          sampleName: lastMessage.sample_name as string,
          stepType: lastMessage.step_type as 'model' | 'merge',
          stepIndex,
          totalSteps: lastMessage.total_steps as number,
          modelName: lastMessage.model_name as string | null,
          status,
          completedSteps
        }
      })
    }

    // Clear ensemble progress when job completes
    if (lastMessage?.event === 'job_completed' && lastMessage.job_id === jobId) {
      setEnsembleProgress(null)
    }
  }, [lastMessage, jobId])

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this job?')) return
    
    setCancelling(true)
    try {
      await cancelJob(jobId)
      setJob(prev => prev ? { ...prev, status: 'cancelled' } : null)
    } catch {
      alert('Failed to cancel job')
    } finally {
      setCancelling(false)
    }
  }

  if (loading) {
    return (
      <div className="card flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    )
  }

  if (!job) {
    return (
      <div className="card text-center py-16">
        <p className="text-gray-400">Job not found</p>
      </div>
    )
  }

  const modelInfo = MODEL_INFO[job.config.model]
  const statusColors: Record<string, string> = {
    completed: 'status-success',
    running: 'status-warning',
    failed: 'status-error',
    pending: 'status-neutral',
    cancelled: 'status-neutral'
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">{job.name}</h2>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{job.job_id}</p>
          </div>
          
          <div className="flex items-center gap-2">
            {job.status === 'running' && (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="btn-secondary text-sm flex items-center gap-2"
              >
                {cancelling ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <StopCircle className="w-4 h-4" />
                )}
                Cancel
              </button>
            )}
            
            <span className={statusColors[job.status] || 'status-neutral'}>
              {job.status}
            </span>
          </div>
        </div>

        {job.status === 'running' && (
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Progress</span>
              <span className="text-white font-medium">{job.progress.progress_percent.toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
              <div
                className="h-full progress-bar transition-all duration-500"
                style={{ width: `${job.progress.progress_percent}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>{job.progress.completed} / {job.progress.total_samples} samples</span>
              <span>ETA: {formatDuration(job.progress.eta_seconds)}</span>
            </div>

            {/* Ensemble Progress Steps */}
            {ensembleProgress && job.config.model === 'ensemble' && job.config.ensemble_settings && (
              <div className="mt-4 p-3 rounded-xl bg-surface-800/50 border border-primary-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="w-4 h-4 text-primary-400" />
                  <span className="text-sm text-gray-300">
                    Processing: <span className="text-white font-medium">{ensembleProgress.sampleName.replace(/^[a-f0-9]+_/, '')}</span>
                  </span>
                </div>

                {/* Step indicators */}
                <div className="flex items-center gap-1 flex-wrap">
                  {job.config.ensemble_settings.models.map((modelName, idx) => {
                    const stepNum = idx + 1
                    const isCompleted = ensembleProgress.completedSteps.includes(stepNum)
                    const isCurrent = ensembleProgress.stepIndex === stepNum && ensembleProgress.status === 'started'
                    const modelDisplay = MODEL_INFO[modelName]?.description.split(' ')[0] || modelName

                    return (
                      <div key={modelName} className="flex items-center">
                        <div
                          className={`px-2.5 py-1 rounded-lg text-xs flex items-center gap-1.5 transition-all ${
                            isCompleted
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                              : isCurrent
                              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                              : 'bg-surface-700 text-gray-500 border border-white/5'
                          }`}
                        >
                          {isCompleted ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : isCurrent ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <span className="w-3 h-3 flex items-center justify-center text-[10px]">{stepNum}</span>
                          )}
                          {modelDisplay}
                        </div>
                        {idx < job.config.ensemble_settings!.models.length - 1 && (
                          <div className={`w-4 h-px mx-1 ${isCompleted ? 'bg-emerald-500/50' : 'bg-gray-700'}`} />
                        )}
                      </div>
                    )
                  })}

                  {/* Merge step */}
                  <div className="flex items-center">
                    <div className={`w-4 h-px mx-1 ${
                      ensembleProgress.completedSteps.includes(job.config.ensemble_settings!.models.length)
                        ? 'bg-emerald-500/50'
                        : 'bg-gray-700'
                    }`} />
                    <div
                      className={`px-2.5 py-1 rounded-lg text-xs flex items-center gap-1.5 transition-all ${
                        ensembleProgress.completedSteps.includes(ensembleProgress.totalSteps)
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : ensembleProgress.stepType === 'merge' && ensembleProgress.status === 'started'
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                          : 'bg-surface-700 text-gray-500 border border-white/5'
                      }`}
                    >
                      {ensembleProgress.completedSteps.includes(ensembleProgress.totalSteps) ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : ensembleProgress.stepType === 'merge' && ensembleProgress.status === 'started' ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <GitMerge className="w-3 h-3" />
                      )}
                      Merge
                    </div>
                  </div>
                </div>

                {/* Current step description */}
                <div className="mt-2 text-xs text-gray-500">
                  Step {ensembleProgress.stepIndex}/{ensembleProgress.totalSteps}:
                  {ensembleProgress.stepType === 'model' ? (
                    <span className="text-gray-400 ml-1">
                      {ensembleProgress.status === 'started' ? 'Running' : 'Completed'} {ensembleProgress.modelName}
                    </span>
                  ) : (
                    <span className="text-gray-400 ml-1">
                      {ensembleProgress.status === 'started' ? 'Merging stems...' : 'Merge complete'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="stat-card">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
              <Activity className="w-3.5 h-3.5" />
              Samples
            </div>
            <div className="text-xl font-bold">
              {job.progress.completed}
              <span className="text-gray-500 text-sm font-normal">/{job.progress.total_samples}</span>
            </div>
            {job.progress.failed > 0 && (
              <div className="text-xs text-red-400 mt-1">
                {job.progress.failed} failed
              </div>
            )}
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
              <Clock className="w-3.5 h-3.5" />
              GPU Hours
            </div>
            <div className="text-xl font-bold">
              {job.costs.gpu_hours.toFixed(2)}
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
              <DollarSign className="w-3.5 h-3.5" />
              Cost
            </div>
            <div className="text-xl font-bold text-amber-400">
              {formatCurrency(job.costs.estimated_cost_usd)}
            </div>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
              <Cpu className="w-3.5 h-3.5" />
              Model
            </div>
            <div className="text-sm font-medium">{modelInfo?.description || job.config.model}</div>
            <div className="text-xs text-purple-400">ULTRA Quality</div>
            {job.config.model === 'ensemble' && job.config.ensemble_settings && (
              <div className="mt-2 pt-2 border-t border-white/5">
                <div className="flex items-center gap-1 text-xs text-primary-400">
                  <Layers className="w-3 h-3" />
                  {job.config.ensemble_settings.models.length} models
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {job.config.ensemble_settings.merge_strategy.replace('_', ' ')}
                </div>
              </div>
            )}
          </div>
        </div>

        {job.error && (
          <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <div className="flex items-center gap-2 text-red-400 font-medium text-sm mb-1">
              <XCircle className="w-4 h-4" />
              Error
            </div>
            <p className="text-sm text-gray-300">{job.error}</p>
          </div>
        )}
      </div>

      {job.status === 'completed' && results.length > 0 && (
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <h3 className="section-title">
                <Volume2 className="w-5 h-5 text-primary-400" />
                Results
                <span className="text-sm font-normal text-gray-500">({results.length} samples)</span>
              </h3>

              {/* Compare toggle for ensemble jobs */}
              {(job.config.model === 'ensemble' || results.some(r => r.ensemble_comparison)) && (
                <button
                  onClick={() => setShowComparison(!showComparison)}
                  className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-all ${
                    showComparison
                      ? 'bg-primary-500 text-white'
                      : 'bg-surface-700 text-gray-300 hover:bg-surface-600 border border-white/10'
                  }`}
                >
                  <BarChart3 className="w-3.5 h-3.5" />
                  {showComparison ? 'Hide Comparison' : 'Compare Models'}
                </button>
              )}
            </div>

            {results.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {results.map((sample) => (
                  <button
                    key={sample.sample_name}
                    onClick={() => setSelectedSample(sample.sample_name)}
                    className={`px-2.5 py-1 rounded-lg text-xs transition-all ${
                      selectedSample === sample.sample_name
                        ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                        : 'bg-surface-800/50 text-gray-400 hover:text-white border border-transparent hover:border-white/10'
                    }`}
                  >
                    {sample.sample_name.replace(/^[a-f0-9]+_/, '').slice(0, 15)}...
                  </button>
                ))}
              </div>
            )}
          </div>

          {results.filter(s => s.sample_name === selectedSample).map((sample) => (
            <div key={sample.sample_name}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Music className="w-4 h-4" />
                  <span className="truncate">{sample.sample_name.replace(/^[a-f0-9]+_/, '')}</span>
                </div>
                {sample.models_used && sample.models_used.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary-500/10 border border-primary-500/20">
                      <GitMerge className="w-3.5 h-3.5 text-primary-400" />
                      <span className="text-xs text-primary-300">
                        {sample.models_used.map(m => MODEL_INFO[m]?.description.split(' ')[0] || m).join(' + ')}
                      </span>
                    </div>
                    {sample.merge_strategy && (
                      <span className="text-xs text-gray-500">
                        ({sample.merge_strategy.replace('_', ' ')})
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Play All Controls */}
              <div className="flex items-center gap-2 mb-4 p-2 rounded-lg bg-surface-800/30 border border-white/5">
                <span className="text-xs text-gray-400 mr-2">All Stems:</span>
                {!isPlayingAll ? (
                  <button
                    onClick={handlePlayAll}
                    className="btn-primary text-xs flex items-center gap-1.5 py-1.5 px-3"
                    title="Play all stems together"
                  >
                    <Play className="w-3.5 h-3.5" />
                    Play All
                  </button>
                ) : (
                  <button
                    onClick={handlePauseAll}
                    className="btn-secondary text-xs flex items-center gap-1.5 py-1.5 px-3"
                    title="Pause all stems"
                  >
                    <Pause className="w-3.5 h-3.5" />
                    Pause
                  </button>
                )}
                <button
                  onClick={handleStopAll}
                  className="btn-secondary text-xs flex items-center gap-1.5 py-1.5 px-3"
                  title="Stop and reset all stems"
                >
                  <Square className="w-3 h-3" />
                  Stop
                </button>
                <span className="text-xs text-gray-500 ml-2">
                  Hear all {sample.stems.length} stems playing simultaneously
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {sample.stems.map((stem) => {
                  const effectivePath = getEffectiveStemPath(stem.name, stem.file_path)
                  const isOverridden = stemOverrides[stem.name] !== undefined
                  const overrideModel = stemOverrides[stem.name]?.modelName
                  const hasDrumSubStems = stem.name === 'drums' && stem.sub_stems && stem.sub_stems.length > 0

                  return (
                    <div key={stem.name} className={hasDrumSubStems ? "lg:col-span-2" : ""}>
                      {/* Main stem player */}
                      <div className="relative">
                        {isOverridden && (
                          <div className="absolute -top-1 -right-1 z-10 px-1.5 py-0.5 rounded text-[10px] bg-amber-500 text-white font-medium">
                            {overrideModel}
                          </div>
                        )}
                        {hasDrumSubStems && (
                          <div className="flex items-center gap-2 mb-2">
                            <button
                              onClick={() => setExpandedDrums(!expandedDrums)}
                              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
                            >
                              {expandedDrums ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                              <span className="font-medium">
                                Drums ({stem.sub_stems?.length || 0} elements)
                              </span>
                            </button>
                          </div>
                        )}
                        <AudioPlayer
                          key={`${stem.name}-${effectivePath}`}
                          ref={(ref) => registerPlayerRef(stem.name, ref)}
                          name={hasDrumSubStems ? "drums (full mix)" : stem.name}
                          src={effectivePath}
                          onPlayStateChange={(playing) => {
                            if (!playing) {
                              const anyPlaying = Array.from(playerRefs.current.values()).some(r => r.getIsPlaying())
                              if (!anyPlaying) setIsPlayingAll(false)
                            }
                          }}
                        />
                      </div>

                      {/* Drum sub-stems (expandable) */}
                      {hasDrumSubStems && expandedDrums && (
                        <div className="mt-3 pl-6 grid grid-cols-1 lg:grid-cols-2 gap-3 border-l-2 border-primary-500/30">
                          {stem.sub_stems?.map((subStem) => {
                            const subEffectivePath = getEffectiveStemPath(subStem.name, subStem.file_path)
                            return (
                              <div key={subStem.name} className="relative">
                                <AudioPlayer
                                  key={`${subStem.name}-${subEffectivePath}`}
                                  ref={(ref) => registerPlayerRef(`drums_${subStem.name}`, ref)}
                                  name={subStem.name}
                                  src={subEffectivePath}
                                  onPlayStateChange={(playing) => {
                                    if (!playing) {
                                      const anyPlaying = Array.from(playerRefs.current.values()).some(r => r.getIsPlaying())
                                      if (!anyPlaying) setIsPlayingAll(false)
                                    }
                                  }}
                                />
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-white/5">
                {sample.stems.map((stem) => {
                  const effectivePath = getEffectiveStemPath(stem.name, stem.file_path)
                  const isOverridden = stemOverrides[stem.name] !== undefined
                  const hasDrumSubStems = stem.name === 'drums' && stem.sub_stems && stem.sub_stems.length > 0

                  return (
                    <div key={stem.name} className="contents">
                      {/* Main stem download */}
                      <a
                        href={effectivePath}
                        download
                        className={`btn-secondary text-xs flex items-center gap-1.5 py-1.5 px-3 ${isOverridden ? 'border-amber-500/50' : ''}`}
                        title={isOverridden ? `Downloading from ${stemOverrides[stem.name]?.modelName}` : undefined}
                      >
                        <Download className="w-3 h-3" />
                        {hasDrumSubStems ? 'drums (full)' : stem.name}
                        {isOverridden && <span className="text-amber-400">*</span>}
                      </a>

                      {/* Drum sub-stem downloads */}
                      {hasDrumSubStems && stem.sub_stems?.map((subStem) => (
                        <Fragment key={subStem.name}>
                          <a
                            href={subStem.file_path}
                            download
                            className="btn-secondary text-xs flex items-center gap-1.5 py-1.5 px-3 opacity-80 hover:opacity-100"
                            title={`Download ${subStem.name} only`}
                          >
                            <Download className="w-3 h-3" />
                            {subStem.name}
                          </a>
                          {subStem.transcription && (
                            <a
                              href={subStem.transcription.midi_path}
                              download
                              className="btn-secondary text-xs flex items-center gap-1.5 py-1.5 px-3 border-purple-500/30 hover:border-purple-500/50 opacity-80 hover:opacity-100"
                              title={subStem.transcription.stats ? `${subStem.transcription.stats.notes} notes` : `Download ${subStem.name} MIDI`}
                            >
                              <Music className="w-3 h-3" />
                              {subStem.name} MIDI
                            </a>
                          )}
                        </Fragment>
                      ))}

                      {/* MIDI download button if transcription exists */}
                      {stem.transcription && (
                        <a
                          href={stem.transcription.midi_path}
                          download
                          className="btn-secondary text-xs flex items-center gap-1.5 py-1.5 px-3 border-purple-500/30 hover:border-purple-500/50"
                          title={stem.transcription.stats ? `${stem.transcription.stats.notes} notes, ${stem.transcription.stats.duration_sec.toFixed(1)}s` : 'Download MIDI transcription'}
                        >
                          <Music className="w-3 h-3" />
                          MIDI
                        </a>
                      )}
                    </div>
                  )
                })}
                <a
                  href={getStemsZipUrl(jobId, sample.sample_name, job?.name ? `${job.name}_stems` : undefined)}
                  download
                  className="btn-primary text-xs flex items-center gap-1.5 py-1.5 px-3"
                  title="Download all stems as a ZIP folder (merged versions)"
                >
                  <FolderArchive className="w-3.5 h-3.5" />
                  Download as ZIP
                </a>
                {(sample.stems.some(s => s.transcription) || sample.stems.some(s => s.sub_stems?.some(ss => ss.transcription)) || (sample.transcriptions && sample.transcriptions.length > 0)) && (
                  <a
                    href={getMidiZipUrl(jobId, sample.sample_name, job?.name ? `${job.name}_midi` : undefined)}
                    download
                    className="btn-secondary text-xs flex items-center gap-1.5 py-1.5 px-3 border-purple-500/30 hover:border-purple-500/50"
                    title="Download all MIDI transcriptions as a ZIP folder"
                  >
                    <Music className="w-3.5 h-3.5" />
                    Download MIDI Only
                  </a>
                )}
              </div>

              {/* MIDI Transcriptions Section */}
              {(sample.stems.some(s => s.transcription) || sample.stems.some(s => s.sub_stems?.some(ss => ss.transcription)) || (sample.transcriptions && sample.transcriptions.length > 0)) && (
                <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Music className="w-4 h-4 text-purple-400" />
                    <h4 className="text-sm font-medium text-purple-300">MIDI Transcriptions</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {/* Stem-level transcriptions */}
                    {sample.stems
                      .filter(stem => stem.transcription)
                      .map(stem => (
                        <a
                          key={stem.transcription!.stem_name}
                          href={stem.transcription!.midi_path}
                          download
                          className="flex items-center justify-between p-3 bg-surface-800/50 hover:bg-surface-700/50 border border-white/5 hover:border-purple-500/50 rounded-lg transition-all group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <Music className="w-4 h-4 text-purple-400" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-white capitalize">
                                {stem.transcription!.stem_name}
                              </div>
                              {stem.transcription!.stats && (
                                <div className="text-xs text-gray-400">
                                  {stem.transcription!.stats.notes} notes • {stem.transcription!.stats.duration_sec.toFixed(1)}s
                                </div>
                              )}
                            </div>
                          </div>
                          <Download className="w-4 h-4 text-gray-400 group-hover:text-purple-400 transition-colors" />
                        </a>
                      ))}
                    {/* Drum sub-stem transcriptions */}
                    {sample.stems
                      .filter(stem => stem.sub_stems?.some(ss => ss.transcription))
                      .flatMap(stem => stem.sub_stems!.filter(ss => ss.transcription))
                      .map(subStem => (
                        <a
                          key={`drum_${subStem.transcription!.stem_name}`}
                          href={subStem.transcription!.midi_path}
                          download
                          className="flex items-center justify-between p-3 bg-surface-800/50 hover:bg-surface-700/50 border border-white/5 hover:border-purple-500/50 rounded-lg transition-all group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <Music className="w-4 h-4 text-purple-400" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-white capitalize">
                                {subStem.transcription!.stem_name}
                              </div>
                              {subStem.transcription!.stats && (
                                <div className="text-xs text-gray-400">
                                  {subStem.transcription!.stats.notes} notes • {subStem.transcription!.stats.duration_sec.toFixed(1)}s
                                </div>
                              )}
                            </div>
                          </div>
                          <Download className="w-4 h-4 text-gray-400 group-hover:text-purple-400 transition-colors" />
                        </a>
                      ))}
                    {/* Sample-level transcriptions (fallback for legacy jobs) */}
                    {sample.transcriptions?.map(trans => (
                      <a
                        key={trans.stem_name}
                        href={trans.midi_path}
                        download
                        className="flex items-center justify-between p-3 bg-surface-800/50 hover:bg-surface-700/50 border border-white/5 hover:border-purple-500/50 rounded-lg transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                            <Music className="w-4 h-4 text-purple-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white capitalize">
                              {trans.stem_name.replace('drums_', '')}
                            </div>
                            {trans.stats && (
                              <div className="text-xs text-gray-400">
                                {trans.stats.notes} notes • {trans.stats.duration_sec.toFixed(1)}s
                              </div>
                            )}
                          </div>
                        </div>
                        <Download className="w-4 h-4 text-gray-400 group-hover:text-purple-400 transition-colors" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Ensemble Comparison View */}
              {showComparison && (
                sample.ensemble_comparison ? (
                  <EnsembleComparison
                    comparison={sample.ensemble_comparison}
                    overrides={stemOverrides}
                    onOverrideChange={handleOverrideChange}
                  />
                ) : (
                  <div className="mt-4 p-4 rounded-xl bg-surface-800/50 border border-amber-500/20">
                    <div className="flex items-center gap-2 text-amber-400 text-sm">
                      <BarChart3 className="w-4 h-4" />
                      <span>Comparison data not available for this job.</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Run a new ensemble job to see per-model comparison with SNR values.
                    </p>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
